import authManager from '../auth-manager';
import { localCache, cacheKeys } from '../app-cache';
import { Logger } from '@aws-amplify/core';

/*******************************************************************************
 * startup-manager.js is a collection of functions to assist with the startup
 * routine of the app (splash screen config, authentication, remote config, etc)
 ******************************************************************************/

const log = new Logger('startup-manager');

/*******************************************************************************
 * States of the startup sequence
 ******************************************************************************/
const states = {
  AUTHENTICATING: 'AUTHENTICATING',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  AUTHENTICATED: 'AUTHENTICATED',
  COMPLETE: 'COMPLETE',
  MOCKED: 'MOCKED',
};

/*******************************************************************************
 * @typedef {object} StartupContext
 * @property {boolean} handleLoginResult
 ******************************************************************************/

/*******************************************************************************
 * Executes the startup sequence and returns an object that indicates the state
 * along with any contextual data related to that state
 * @returns {{state:string, context:StartupContext}}
 ******************************************************************************/
const startUpSequence = async () => {
  const returnObj: any = { state: 'INIT', context: {} };

  if (import.meta.env.VITE_MOCK === 'true') {
    returnObj.state = states.MOCKED;        
    localCache.setItem(cacheKeys.token, '12345');
    return returnObj;
  }

  try {
    returnObj.state = states.AUTHENTICATING;
    const loggedIn = await authManager.handleLogin();
    returnObj.context.handleLoginResult = loggedIn;
  } catch (error) {
    log.error(error);
    returnObj.state = states.AUTHENTICATION_ERROR;
    returnObj.error = error;
    return returnObj;
  }

  // Authenticating a user may require a redirection to cognito/azure.
  // If the user is authenticated, then a redirect isn't required or has
  // already happened and the user has been navigated back to the app.
  if (returnObj.context.handleLoginResult) {
    returnObj.state = states.AUTHENTICATED;
  } else {
    // still authenticating, return here as a redirect is imminent
    returnObj.state = states.AUTHENTICATING;
    return returnObj;
  }

  returnObj.state = states.COMPLETE;
  return returnObj;
};

/*******************************************************************************
 * exported api definition
 ******************************************************************************/
const startupManager = {
  startUpSequence,
  states,
};

export default startupManager;
